import { NameAndId } from "./Transaction";

export type BlackScholesDetails = {
  grants: BlackScholesGrantDetails[];
  subsidiaryCompanies: { name: string; id: string }[];
};

export type ExpectedLifeCalculationMethod =
  | "AGGREGATED_VESTING"
  | "TRANCHE_WISE";

export const EXPECTED_LIFE_CALCULATION_METHOD_LABELS: Record<
  ExpectedLifeCalculationMethod,
  string
> = {
  AGGREGATED_VESTING: "Aggregated Vesting Method",
  TRANCHE_WISE: "Tranche-wise Expected Life Method",
};

export type BlackScholesGrantDetails = {
  employee: {
    id: string;
    name: string;
    status: string;
    type: string;
    subsidiaryCompanyId: string;
  };
  id: string;
  company: {
    name: string;
    id: string;
  };
  state: string;
  dateOfGrant: string;
  strikePrice: number;
  identifier: string;
  grantedOptions: number;
  plan: {
    id: string;
    name: string;
    type: string;
  };
};

export interface BlackScholesV2 {
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
  valuationDate?: Date;
  filters?: {
    planIds?: NameAndId[];
    employmentType?: string[];
    employeeStatus?: string[];
    grantStates?: string[];
    subsidiaryIds?: NameAndId[];
  };
  optionsExpiryYears?: number;
  optionsExpiryFromPlanMaturity?: boolean;
  expectedLifeCalculationMethod?: ExpectedLifeCalculationMethod;
  groupByGrantDateAndStrikePrice?: boolean;
  grantDatesWithData: {
    [key: string]: BlackScholes;
  };
  filteredGrants: BlackScholesGrantDetails[];
  alignmentType?: AlignmentType;
  calculatedData?: {
    [key: string]: BlackScholes;
  };
}

export type AlignmentType =
  | "unique"
  | "month"
  | "quarter"
  | "halfYear"
  | "year"
  | "custom";

export interface BlackScholes {
  valuationDate?: Date; // Date of valuation
  stockPrice: number; // Current stock price
  riskFreeRate: number; // Risk-free interest rate (annualized)
  volatility: number; // Volatility of the underlying stock (annualized)
  dividendYield: number; // Dividend yield of the stock (annualized)
  values?: {
    wavp: number; // Weighted Average Valuation Price
    originalTimeToMaturity: number; // Original time to maturity in years
    adjustedTimeToMaturity: number; // Adjusted time to maturity in years
    yearsElapsed: number;
    fmv: number; // Total Black-Scholes value per option
    d1: number;
    d2: number;
  };
}

export type BlackScholesReportMetaData = {
  filters?: {
    planIds?: NameAndId[];
    employmentType?: string[];
    employeeStatus?: string[];
    grantStates?: string[];
    subsidiaryIds?: NameAndId[];
  };
  groupByGrantDateAndStrikePrice?: boolean;
  optionsExpiryFromPlanMaturity?: boolean;
  optionsExpiryYears?: number;
  expectedLifeCalculationMethod?: ExpectedLifeCalculationMethod;
  alignmentType?: AlignmentType;
  grantsCount?: number;
  totalOptions?: number;
  totalPlans?: number;
  totalEmployees?: number;
};

export type BlackScholesReport = {
  id: string;
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
  valuationDate?: Date;
  metaData?: BlackScholesReportMetaData;
  createdAt: string;
  updatedAt: string;
  state: string;
};
